import { gsap } from 'gsap';

const DEFAULT_DURATIONS = {
  lift: 1.4,
  markerStagger: 0.11,
  markerPop: 0.45,
  trackNameFade: 0.7,
};

/**
 * @param {THREE.Object3D} root
 * @returns {{ type: 'uniform' | 'opacity'; obj: { value?: number; opacity?: number }; value: number }[]}
 */
function collectMeshOpacities(root) {
  /** @type {{ type: 'uniform' | 'opacity'; obj: { value?: number; opacity?: number }; value: number }[]} */
  const entries = [];

  root.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    for (const mat of materials) {
      if (mat.uniforms?.uOpacity) {
        entries.push({
          type: 'uniform',
          obj: mat.uniforms.uOpacity,
          value: mat.uniforms.uOpacity.value,
        });
      } else if (typeof mat.opacity === 'number') {
        entries.push({
          type: 'opacity',
          obj: mat,
          value: mat.opacity,
        });
      }
    }
  });

  return entries;
}

/**
 * @param {{ type: 'uniform' | 'opacity'; obj: { value?: number; opacity?: number }; value: number }[]} entries
 * @param {number} opacity
 */
function applyOpacityEntries(entries, opacity) {
  for (const entry of entries) {
    if (entry.type === 'uniform') {
      entry.obj.value = opacity;
    } else {
      entry.obj.opacity = opacity;
    }
  }
}

/**
 * @param {{ type: 'uniform' | 'opacity'; obj: { value?: number; opacity?: number }; value: number }[]} entries
 */
function restoreOpacityEntries(entries) {
  for (const entry of entries) {
    if (entry.type === 'uniform') {
      entry.obj.value = entry.value;
    } else {
      entry.obj.opacity = entry.value;
    }
  }
}

/**
 * @param {THREE.Object3D} root
 * @returns {{ type: 'uniform' | 'opacity'; obj: { value?: number; opacity?: number }; value: number }[]}
 */
function ensureOpacityEntries(root) {
  if (!root.userData._introOpacityEntries) {
    root.userData._introOpacityEntries = collectMeshOpacities(root);
  }
  return root.userData._introOpacityEntries;
}

/**
 * @param {{
 *   circuitGroup: THREE.Group;
 *   curtainMesh: THREE.Mesh | null;
 *   hologramMaterial: THREE.ShaderMaterial | null;
 *   trackNameLabel: { mesh: THREE.Mesh } | null;
 *   getMarkers: () => THREE.Group[];
 *   getLiftY: () => number;
 *   getSettings: () => {
 *     trackLift: number;
 *     curtainOpacity: number;
 *     pulseAmount: number;
 *     showElevationCurtain: boolean;
 *     trackNameOpacity: number;
 *     showTrackName: boolean;
 *   };
 *   getDurations?: () => Partial<typeof DEFAULT_DURATIONS>;
 *   onPlayingChange?: (playing: boolean) => void;
 *   onComplete?: () => void;
 *   revealedInitially?: boolean;
 * }}
 */
export function createIntroTimeline(ctx) {
  const {
    circuitGroup,
    curtainMesh,
    hologramMaterial,
    trackNameLabel,
    getMarkers,
    getLiftY,
    getSettings,
    getDurations = () => ({}),
    onPlayingChange,
    onComplete,
    revealedInitially = false,
  } = ctx;

  let timeline = null;
  let complete = false;

  function durations() {
    return { ...DEFAULT_DURATIONS, ...getDurations() };
  }

  /** @returns {{ marker: THREE.Group; labelObject: THREE.Object3D | null }[]} */
  function getMarkerEntries() {
    return getMarkers().map((marker) => ({
      marker,
      labelObject: marker.userData.cornerLabel?.object ?? null,
    }));
  }

  function captureMarkerStates() {
    for (const { marker, labelObject } of getMarkerEntries()) {
      ensureOpacityEntries(marker);
      if (labelObject) ensureOpacityEntries(labelObject);
    }
  }

  function applyStartState() {
    const liftY = getLiftY();
    const settings = getSettings();

    circuitGroup.position.y = -liftY;

    if (curtainMesh) {
      curtainMesh.visible =
        settings.showElevationCurtain && settings.trackLift > 0.02;
    }

    if (hologramMaterial) {
      hologramMaterial.uniforms.uCurtainOpacity.value = 0;
      hologramMaterial.uniforms.uPulseAmount.value = 0;
    }

    captureMarkerStates();

    for (const { marker, labelObject } of getMarkerEntries()) {
      marker.scale.setScalar(0);
      applyOpacityEntries(marker.userData._introOpacityEntries, 0);

      if (labelObject) {
        labelObject.visible = false;
        applyOpacityEntries(labelObject.userData._introOpacityEntries, 0);
      }
    }

    if (trackNameLabel?.mesh) {
      const show =
        settings.showTrackName && settings.trackNameText?.trim().length > 0;
      trackNameLabel.mesh.visible = show;
      if (show) {
        if (trackNameLabel.mesh.userData._introTargetOpacity == null) {
          trackNameLabel.mesh.userData._introTargetOpacity =
            settings.trackNameOpacity;
        }
        trackNameLabel.mesh.material.opacity = 0;
      }
    }

    complete = false;
  }

  function applyEndState() {
    const settings = getSettings();

    circuitGroup.position.y = 0;

    if (hologramMaterial) {
      hologramMaterial.uniforms.uCurtainOpacity.value = settings.curtainOpacity;
      hologramMaterial.uniforms.uPulseAmount.value = settings.pulseAmount;
    }

    captureMarkerStates();

    for (const { marker, labelObject } of getMarkerEntries()) {
      marker.scale.setScalar(1);
      restoreOpacityEntries(marker.userData._introOpacityEntries);

      if (labelObject) {
        labelObject.visible = true;
        restoreOpacityEntries(labelObject.userData._introOpacityEntries);
      }
    }

    if (trackNameLabel?.mesh) {
      const target =
        trackNameLabel.mesh.userData._introTargetOpacity ??
        settings.trackNameOpacity;
      trackNameLabel.mesh.material.opacity = target;
    }

    complete = true;
  }

  function addOpacityTweens(tl, entries, startTime, duration) {
    for (const entry of entries) {
      if (entry.type === 'uniform') {
        tl.to(
          entry.obj,
          { value: entry.value, duration, ease: 'power2.out' },
          startTime,
        );
      } else {
        tl.to(
          entry.obj,
          { opacity: entry.value, duration, ease: 'power2.out' },
          startTime,
        );
      }
    }
  }

  function buildTimeline() {
    timeline?.kill();

    const d = durations();
    const settings = getSettings();
    const entries = getMarkerEntries();

    captureMarkerStates();

    const tl = gsap.timeline({
      paused: true,
      onStart: () => onPlayingChange?.(true),
      onPause: () => onPlayingChange?.(false),
      onComplete: () => {
        complete = true;
        onPlayingChange?.(false);
        onComplete?.();
      },
    });

    tl.to(
      circuitGroup.position,
      { y: 0, duration: d.lift, ease: 'power2.inOut' },
      0,
    );

    if (hologramMaterial) {
      tl.to(
        hologramMaterial.uniforms.uCurtainOpacity,
        {
          value: settings.curtainOpacity,
          duration: d.lift,
          ease: 'power2.inOut',
        },
        0,
      );
      tl.to(
        hologramMaterial.uniforms.uPulseAmount,
        {
          value: settings.pulseAmount,
          duration: d.lift,
          ease: 'power2.inOut',
        },
        0,
      );
    }

    const markerPhaseStart = d.lift;
    const showTrackName =
      settings.showTrackName && settings.trackNameText?.trim().length > 0;

    if (trackNameLabel?.mesh && showTrackName) {
      const target =
        trackNameLabel.mesh.userData._introTargetOpacity ??
        settings.trackNameOpacity;
      trackNameLabel.mesh.visible = true;
      tl.to(
        trackNameLabel.mesh.material,
        { opacity: target, duration: d.trackNameFade, ease: 'power2.out' },
        markerPhaseStart,
      );
    }

    entries.forEach(({ marker, labelObject }, index) => {
      const start = markerPhaseStart + index * d.markerStagger;
      const opacityDuration = d.markerPop * 0.85;

      tl.to(
        marker.scale,
        {
          x: 1,
          y: 1,
          z: 1,
          duration: d.markerPop,
          ease: 'back.out(1.4)',
        },
        start,
      );

      addOpacityTweens(
        tl,
        marker.userData._introOpacityEntries,
        start,
        opacityDuration,
      );

      if (labelObject) {
        tl.call(
          () => {
            labelObject.visible = true;
          },
          null,
          start,
        );
        addOpacityTweens(
          tl,
          labelObject.userData._introOpacityEntries,
          start + 0.05,
          opacityDuration,
        );
      }
    });

    timeline = tl;
    return tl;
  }

  function play() {
    if (!timeline || timeline.progress() === 1) {
      applyStartState();
      buildTimeline();
    }
    timeline.play();
    onPlayingChange?.(true);
  }

  function pause() {
    timeline?.pause();
    onPlayingChange?.(false);
  }

  function isPlaying() {
    return Boolean(timeline?.isActive() && !timeline.paused());
  }

  function isComplete() {
    return complete;
  }

  function rebuild() {
    const wasPlaying = isPlaying();
    const progress = timeline?.progress() ?? 0;
    buildTimeline();
    applyStartState();
    if (wasPlaying && progress > 0) {
      timeline.progress(progress);
      timeline.play();
    }
  }

  buildTimeline();
  if (revealedInitially) {
    applyEndState();
  } else {
    applyStartState();
  }

  return {
    timeline: () => timeline,
    applyStartState,
    applyEndState,
    rebuild,
    play,
    pause,
    isPlaying,
    isComplete,
  };
}
